// app/ClientLayoutWrapper.tsx
'use client'; // This component runs on the client

import { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic'; // Import dynamic
import Lenis from 'lenis';
import AuthProvider from '../context/AuthContext';
import '../lib/gsap-init'; // Import GSAP initialization

// Dynamically import MoonPayProvider
const MoonPayProvider = dynamic(
  () => import('@moonpay/moonpay-react').then((mod) => mod.MoonPayProvider),
  { ssr: false },
);

// This component just wraps its children and applies the Lenis effect
export default function ClientLayoutWrapper({ children }: { children: React.ReactNode }) {
  const lenisRef = useRef<Lenis | null>(null);
  const moonpayApiKey = process.env.NEXT_PUBLIC_MOONPAY_PUBLISHABLE_KEY;
  
  useEffect(() => {
    if (!moonpayApiKey) {
      console.warn('NEXT_PUBLIC_MOONPAY_PUBLISHABLE_KEY is not set. MoonPay components might not work correctly.');
    }
    // Initialize smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
    });
    
    lenisRef.current = lenis;
    
    // Function to handle modal scroll issue
    const handleModalScroll = () => {
      const web3ModalElement = document.querySelector('w3m-modal');
      const isPurchaseModalOpen = document.body.classList.contains('modal-open');
      // Ensure web3ModalElement is not null before accessing classList
      const isWeb3ModalOpen = web3ModalElement && web3ModalElement.classList.contains('open');
      
      if (isWeb3ModalOpen || isPurchaseModalOpen) {
        lenis.stop(); // Stop Lenis when modal is open
      } else {
        lenis.start(); // Start Lenis when modal is closed
      }
    };
    
    // Set up a mutation observer to detect when the modal changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          (mutation.type === 'childList' && mutation.addedNodes.length > 0) || 
          (mutation.type === 'attributes' && mutation.attributeName === 'class')
        ) {
          setTimeout(handleModalScroll, 0);
        }
      });
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    
    const setupModalPrevent = () => {
      const modalElements = document.querySelectorAll('w3m-modal, w3m-router, [data-lenis-prevent]');
      modalElements.forEach(el => {
        if (el) el.setAttribute('data-lenis-prevent', '');
      });
    };
    
    const preventInterval = setInterval(() => {
      setupModalPrevent();
    }, 1000);
    
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    const rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      clearInterval(preventInterval);
      if (lenisRef.current) {
        lenisRef.current.destroy();
      }
    };
  }, [moonpayApiKey]); // Empty dependency array ensures this runs only once on mount

  // Render the children passed down from the layout, wrapped in AuthProvider and MoonPayProvider
  // Ensure apiKey is provided; MoonPayProvider might throw an error or not work if it's undefined/empty.
  return (
    <AuthProvider>
      {moonpayApiKey ? (
        <MoonPayProvider 
          apiKey={moonpayApiKey} 
          debug={process.env.NODE_ENV === 'development'}
        >
          {children}
        </MoonPayProvider>
      ) : (
        // Fallback if API key is missing. 
        // Consider if you want to render children at all or show an error/placeholder.
        <>{children}</> 
      )}
    </AuthProvider>
  );
}